import { getFields, initializeFieldResponse } from "common";
import React, { useEffect, useMemo } from "react";
import { Form, Package, UserDetailsRequirement } from "types";
import shallow from "zustand/shallow";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { Loader } from "../Loader";
import { CreatePuchaseSharedPackageDemographicPicker } from "./CreatePurchasePackageDemographicPicker";
import { CreateSharedPackageExperiences } from "./CreateSharedPackageExperiences";
import { useCustomerDetailsStore } from "./store/customerDetailsStore";
import { useProducts } from "@/hooks/resources/useProduct";
import { getExperiencesInPackage } from "@/models/Package";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { showPackages } from "@/stores/globalStore";

interface CreatePurchasePackageFieldsProps {
    pkg: Package;
}

export const CreatePurchaseSharedPackageFields = ({ pkg }: CreatePurchasePackageFieldsProps) => {
    const [purchase, items] = useCreatePurchaseStore((state) => [state.purchase, state.purchase.items], shallow);

    const [showCustomerFields, nameRequired] = useCustomerDetailsStore(
        (state) => [state.showCustomerFields, state.nameRequired],
        shallow,
    );
    const {
        filteredProducts: { experiences = [] },
        isLoading,
    } = useProducts();
    const { form, guests, formResponse } = items[0];

    const packageExperiences = useMemo(() => {
        return getExperiencesInPackage(pkg, experiences);
    }, [experiences, pkg]);

    useEffect(() => {
        if (!nameRequired) {
            const isNameRequired = packageExperiences.some(
                (experience) => experience.customerNamePreference === UserDetailsRequirement.REQUIRED,
            );
            useCustomerDetailsStore.setState({
                nameRequired: isNameRequired,
            });
        }

        if (!showCustomerFields) {
            const isAllExperienceCustomerNotCollected = packageExperiences.every(
                (experience) => experience.customerNamePreference === UserDetailsRequirement.NOT_COLLECTED,
            );
            useCustomerDetailsStore.setState({
                showCustomerFields: !isAllExperienceCustomerNotCollected,
            });
        }
    }, [packageExperiences, showCustomerFields, nameRequired]);

    const canUsePackages = showPackages();

    useEffect(() => {
        if (canUsePackages && form?.organizerOnly && guests && !formResponse) {
            useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                const { name } = guests[0];
                const initialFormResponse = getInitialFormResponse(form);

                for (const item of draft.items) {
                    item.formResponse = {
                        ...initialFormResponse,
                        fields: initialFormResponse.fields,
                        name: name,
                    };
                }
            });
        }
    }, [form, formResponse, guests, canUsePackages]);

    if (!canUsePackages) {
        return null;
    }

    return (
        <div className="grid grid-cols-12">
            <div className="col-span-9">
                <div className="mb-6 w-full rounded bg-gray-lighter p-4">
                    <div className="flex items-center space-x-1">
                        <div className="shrink-0">
                            <ProductImage product={pkg} size="small" className="!mr-1" />
                        </div>

                        <NoTranslate as="h4" className="font-bold">
                            {pkg.name}
                        </NoTranslate>
                    </div>
                </div>
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        <div className="mb-4 w-full">
                            <CreatePuchaseSharedPackageDemographicPicker
                                pkg={pkg}
                                packageExperiences={packageExperiences}
                            />
                        </div>

                        <CreateSharedPackageExperiences
                            packageExperiences={packageExperiences}
                            items={purchase.items}
                            pkg={pkg}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const getInitialFormResponse = (form: Form) => {
    return {
        fields: getFields(form).map((field) => ({ ...initializeFieldResponse(field), value: "" })),
    };
};
